<script lang="ts">
  import { tick } from 'svelte';
  import Button from '$src/js/components/molecules/button/Button.svelte';
  import Checkbox from '$src/js/components/molecules/checkbox/Checkbox.svelte';
  import Countdown from '$src/js/components/molecules/countdown/Countdown.svelte';
  import VoteDone from '$src/js/components/organisms/vote/VoteDone.svelte';
  import BaseModule from '$src/js/modules/base/BaseModule';
  import { VoteState } from '$src/js/states/VoteState.svelte';

  interface ScoreResponse {
    status: string;
    data: { dark: number; light: number };
  }

  interface VoteResponse {
    status: string;
    message?: string;
  }

  let selectedJersey = $state<'dark' | 'light' | null>(null);
  let confirmEl = $state<HTMLSpanElement | undefined>();
  let successEl = $state<HTMLSpanElement | undefined>();
  let firstname = $state('');
  let email = $state('');
  let terms = $state<boolean>(false);
  let marketing = $state<boolean>(false);

  let submitting = $state(false);
  let submitted = $state(false);
  let apiError = $state('');

  let scores = $state<{ dark: number; light: number } | null>(null);

  const selectJersey = (jersey: 'dark' | 'light'): void => {
    selectedJersey = jersey;

    if (!confirmEl) {
      return;
    }

    BaseModule.getInstance().scroll.scrollToElement(confirmEl);
  };

  const fetchScores = async () => {
    try {
      const response = await fetch('/api-simple/?module=jersey&action=current-score');
      const json = (await response.json()) as ScoreResponse;
      if (json.status === 'success') {
        scores = json.data;
      }
    } catch {
      // silently fail, scores simply won't be shown
    }
  };

  $effect(() => {
    void fetchScores();
  });

  let errors = $state({
    jersey: '',
    firstname: '',
    email: '',
    terms: '',
  });

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validate = () => {
    const next = { jersey: '', firstname: '', email: '', terms: '' };
    let valid = true;

    if (!selectedJersey) {
      next.jersey = BaseModule.getInstance().i18n.translate('vote_form_error_jersey');
      valid = false;
    }
    if (!firstname.trim()) {
      next.firstname = BaseModule.getInstance().i18n.translate('vote_form_error_required');
      valid = false;
    }
    if (!email.trim()) {
      next.email = BaseModule.getInstance().i18n.translate('vote_form_error_required');
      valid = false;
    } else if (!validateEmail(email)) {
      next.email = BaseModule.getInstance().i18n.translate('vote_form_error_email_invalid');
      valid = false;
    }
    if (!terms) {
      next.terms = BaseModule.getInstance().i18n.translate('vote_form_error_required');
      valid = false;
    }

    errors = next;
    return valid;
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!validate()) return;

    submitting = true;

    const formData = new FormData();
    formData.append('firstname', firstname);
    formData.append('email', email);
    formData.append('jersey', selectedJersey!);

    if (marketing) {
      formData.append('marketing', '1');
    }

    apiError = '';

    try {
      const response = await fetch('/api-simple/?module=jersey&action=vote', {
        method: 'POST',
        body: formData,
      });

      const json = (await response.json()) as VoteResponse;

      if (json.status === 'success') {
        submitted = true;
        await tick();

        if (successEl) {
          BaseModule.getInstance().scroll.scrollToElement(successEl);
        }
      } else {
        const errorMap: Record<string, string> = {
          ERROR_MISSING_REQUIRED_PARAMETERS: 'vote_form_error_missing_parameters',
          ERROR_INVALID_EMAIL: 'vote_form_error_invalid_email',
          ERROR_SOMETHING_WENT_WRONG: 'vote_form_error_something_went_wrong',
        };

        const key =
          (json.message !== undefined ? errorMap[json.message] : undefined) ??
          'vote_form_error_something_went_wrong';
        apiError = BaseModule.getInstance().i18n.translate(key);
      }
    } catch {
      apiError = BaseModule.getInstance().i18n.translate('vote_form_error_something_went_wrong');
    } finally {
      submitting = false;
    }
  };
</script>

{#if VoteState.votingEnded}
  <VoteDone />
{:else}
  <div class="pt-20 md:pt-45" id="vote">
    <div class="mx-auto flex max-w-[1200px] flex-col items-center px-4">
      <h2
        class="font-clorat text-yellow mb-6 text-center text-[40px] leading-[100%] text-balance sm:mb-8 sm:text-[80px] lg:text-[120px]"
      >
        {BaseModule.getInstance().i18n.translate('vote_title')}
      </h2>

      <p
        class="mb-8 max-w-[560px] text-center text-[16px] leading-[180%] font-light sm:mb-16 sm:text-[20px] lg:text-[24px]"
      >
        {BaseModule.getInstance().i18n.translate('vote_text')}
      </p>

      <Countdown />

      {#if submitted}
        <div
          class="flex w-full max-w-[600px] flex-col items-center gap-4 py-16 text-center sm:gap-6"
          bind:this={successEl}
        >
          <span class="font-clorat text-yellow text-[32px] leading-[100%] lg:text-[64px]">
            {BaseModule.getInstance().i18n.translate('vote_success_title')}
          </span>
          <p class="max-w-[400px] text-[18px] leading-[180%] font-light sm:text-[20px]">
            {BaseModule.getInstance().i18n.translate('vote_success_text')}
          </p>
        </div>
      {:else}
        <form class="flex w-full max-w-[600px] flex-col" onsubmit={handleSubmit} novalidate>
          <span
            class="font-clorat mb-8 text-center text-[32px] leading-[100%] sm:mb-12 lg:text-[48px]"
          >
            {BaseModule.getInstance().i18n.translate('vote_form_subtitle_favorite')}
          </span>

          <div class="mx-auto mb-20 flex w-full max-w-[520px] flex-col gap-12 sm:mb-24">
            <div class="xs:flex-row xs:gap-12 mb-16 flex w-full flex-col justify-center gap-32">
              <div class="relative flex flex-1 flex-col items-center gap-2">
                <div
                  class="jersey-option gradient-border gradient-border--rounded xs:w-full aspect-square w-56 rounded-full"
                  class:active={selectedJersey === 'dark'}
                >
                  <img
                    class="h-full w-full rounded-full object-cover"
                    src="https://yellow-b-global.b-cdn.net/production/the-architect/images/thumb1.jpg"
                    alt=""
                  />
                </div>

                <div class="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                  <Button
                    size="small"
                    text={BaseModule.getInstance().i18n.translate('vote_form_button_dark')}
                    type="button"
                    filled={true}
                    onclick={() => selectJersey('dark')}
                  />
                </div>

                {#if scores !== null}
                  <span
                    class="font-clorat text-yellow absolute -bottom-18 left-1/2 -translate-x-1/2 text-center text-2xl leading-[100%] whitespace-nowrap"
                  >
                    {scores.dark}% {BaseModule.getInstance().i18n.translate('vote_percentage')}
                  </span>
                {/if}
              </div>

              <div class="relative flex flex-1 flex-col items-center gap-2">
                <div
                  class="jersey-option gradient-border gradient-border--rounded xs:w-full aspect-square w-56 rounded-full"
                  class:active={selectedJersey === 'light'}
                >
                  <img
                    class="h-full w-full rounded-full object-cover"
                    src="https://yellow-b-global.b-cdn.net/production/the-architect/images/thumb2.jpg"
                    alt=""
                  />
                </div>

                <div class="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                  <Button
                    size="small"
                    text={BaseModule.getInstance().i18n.translate('vote_form_button_light')}
                    type="button"
                    filled={true}
                    onclick={() => selectJersey('light')}
                  />
                </div>

                {#if scores !== null}
                  <span
                    class="font-clorat text-yellow absolute -bottom-18 left-1/2 -translate-x-1/2 text-center text-2xl leading-[100%] whitespace-nowrap"
                  >
                    {scores.light}% {BaseModule.getInstance().i18n.translate('vote_percentage')}
                  </span>
                {/if}
              </div>
            </div>

            {#if errors.jersey}
              <p class="error mb-4">{errors.jersey}</p>
            {/if}
          </div>

          <span
            bind:this={confirmEl}
            class="font-clorat mb-8 text-center text-[32px] leading-[100%] sm:mb-12 lg:text-[48px]"
          >
            {BaseModule.getInstance().i18n.translate('vote_form_subtitle_confirm')}
          </span>

          <div class="mb-8 flex flex-col items-center gap-2 sm:mb-10 sm:gap-4">
            <label
              class="text-sm leading-[150%] font-extrabold tracking-[0.175em] whitespace-nowrap uppercase"
              for="firstname"
            >
              {BaseModule.getInstance().i18n.translate('vote_form_firstname_label')}
            </label>

            <input
              class="h-14 w-full rounded-full border border-white/50 px-4 text-center duration-300 hover:border-white sm:h-18 sm:px-6 sm:text-lg"
              class:border-red-500={!!errors.firstname}
              id="firstname"
              type="text"
              placeholder={BaseModule.getInstance().i18n.translate(
                'vote_form_firstname_placeholder',
              )}
              bind:value={firstname}
            />

            {#if errors.firstname}
              <p class="error">{errors.firstname}</p>
            {/if}
          </div>

          <div class="mb-12 flex flex-col items-center gap-2 sm:mb-14 sm:gap-4">
            <label
              class="text-sm leading-[150%] font-extrabold tracking-[0.175em] whitespace-nowrap uppercase"
              for="email"
            >
              {BaseModule.getInstance().i18n.translate('vote_form_email_label')}
            </label>

            <input
              class="h-14 w-full rounded-full border border-white/50 px-4 text-center duration-300 hover:border-white sm:h-18 sm:px-6 sm:text-lg"
              class:border-red-500={!!errors.email}
              id="email"
              type="email"
              placeholder={BaseModule.getInstance().i18n.translate('vote_form_email_placeholder')}
              bind:value={email}
            />

            {#if errors.email}
              <p class="error">{errors.email}</p>
            {/if}
          </div>

          <div class="mb-4 flex flex-col gap-3">
            <Checkbox
              label={BaseModule.getInstance().i18n.translate('vote_form_marketing_label')}
              id="marketing"
              bind:checked={marketing}
            />
          </div>

          <div class="mb-12 flex flex-col gap-3 sm:mb-14">
            <Checkbox
              label={BaseModule.getInstance().i18n.translate('vote_form_terms_label')}
              id="terms"
              bind:checked={terms}
            />

            {#if errors.terms}
              <p class="error">{errors.terms}</p>
            {/if}
          </div>

          <Button
            size="medium"
            text={BaseModule.getInstance().i18n.translate('vote_form_submit_button')}
            type="submit"
            onclick={() => {}}
            disabled={submitting}
          />

          {#if apiError}
            <p class="error mt-4">{apiError}</p>
          {/if}
        </form>
      {/if}
    </div>
  </div>
{/if}

<style>
  @reference '$src/css/main.css';

  .jersey-option {
    @apply opacity-70 outline-3 outline-offset-4 outline-transparent transition-all duration-500;

    &.active {
      @apply outline-yellow opacity-100;
    }
  }

  .error {
    @apply text-center text-sm font-semibold text-[#ff0000];
  }
</style>
